/**
 * 웹 시스템 알림 표시 — 플랫폼 차이를 흡수하는 단일 진입점 (Tauri 제외, 그쪽은 Rust 경로).
 *
 * ⚠ **Chrome on Android는 `new Notification()`을 영구히 지원하지 않는다.**
 * `'Notification' in window`도 `Notification.permission === 'granted'`도 true라 조건문은 통과하지만,
 * 생성자를 호출하는 순간 `TypeError: Illegal constructor`를 던진다(Chromium #481856 — 페이지가
 * 알림보다 먼저 죽는 모바일 특성 때문에 의도적으로 막았고 앞으로도 구현되지 않는다).
 * 예전에는 호출부에 try/catch가 없어서 이 예외가 `setIncomingCall`까지 함께 날려버렸고,
 * 그래서 안드로이드에서는 시스템 알림뿐 아니라 **통화 수신 배너와 링톤까지 통째로 사라졌다**.
 * iOS PWA는 백그라운드에서 프로세스가 정지해 소켓 경로 자체를 안 밟으므로 이 버그가 드러나지 않았다.
 *
 * 그래서 `ServiceWorkerRegistration.showNotification()`을 먼저 쓰고, SW가 없는 환경에서만
 * 생성자로 폴백한다. **어떤 경우에도 예외를 밖으로 내보내지 않는다** — 알림 하나 못 띄운 것이
 * 호출부의 나머지 로직을 죽이면 안 된다.
 */

import { isWebNotifyEnabled } from './webNotifyPref';

export interface WebNotificationOptions {
  body: string;
  /** 같은 tag끼리 서로를 갈아끼운다. 통화는 `call-{callId}`, 메시지는 `ch-{channelId}`, 시스템 알림은 `sys-{activityId}` */
  tag: string;
  /** 서비스워커의 notificationclick 핸들러가 읽는다 — channelId가 없으면 클릭해도 채널이 안 열린다
   *  (시스템 알림은 `{ kind:'system', source }`로 알림함 탭을 연다) */
  data: Record<string, unknown>;
  silent?: boolean;
  requireInteraction?: boolean;
  vibrate?: number[];
}

/**
 * @param onFallbackClick 생성자 폴백 경로에서만 onclick으로 붙는다.
 *                        SW 경로에서는 SW의 notificationclick이 담당하므로 무시된다.
 * @returns 알림을 실제로 띄웠으면 true
 */
export async function showWebNotification(
  title: string,
  options: WebNotificationOptions,
  onFallbackClick?: () => void
): Promise<boolean> {
  try {
    // "이 기기에서 알림 받기"를 껐으면 화면을 보는 중의 시스템 알림도 띄우지 않는다
    // (백그라운드 푸시는 구독 해제로 이미 끊겨 있다 — 두 경로의 의미를 일치시킨다).
    if (!isWebNotifyEnabled()) return false;
    if (!('Notification' in window) || Notification.permission !== 'granted') return false;

    const withAssets = { icon: '/icon.png', badge: '/icons/badge-72x72.png', ...options };

    // ★ `ready`가 아니라 `getRegistration()`을 쓴다 — `ready`는 SW가 등록되지 않은 환경
    //   (dev localhost 등)에서 영원히 pending이라 호출부를 매달아 놓는다.
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      await reg.showNotification(title, withAssets as NotificationOptions);
      return true;
    }

    const n = new Notification(title, withAssets as NotificationOptions);
    if (onFallbackClick) {
      n.onclick = () => {
        onFallbackClick();
        n.close();
      };
    }
    return true;
  } catch {
    // Android Chrome의 Illegal constructor, 권한 회수, 쿼터 초과 등 — 조용히 실패한다.
    return false;
  }
}

/** `ch-{channelId}` 규약에서 채널 id를 뽑는다. 통화(`call-*`)·폴백(`ina-talk`) tag는 걸리지 않는다 */
const CHANNEL_TAG = /^ch-(\d+)$/;

/**
 * 표시 중인 채널 알림 가운데 **안읽음이 0인 채널의 것만** 닫는다.
 *
 * **두 경로로 동시에 닫는다** — 페이지 컨텍스트에서 직접, 그리고 서비스워커에 위임해서.
 * 안드로이드·데스크톱은 페이지 경로만으로 사라지지만 **iOS(WebKit) PWA는 그러지 않는다**
 * (2026-08-26 실기기 확인: 안드로이드 정상, 아이폰 잔존). 알림을 띄운 주체가 SW라 페이지
 * 컨텍스트 조회가 그것을 돌려주지 않는 것으로 보여, 같은 일을 SW 안에서도 시킨다.
 * `close()`는 멱등이라 두 경로가 겹쳐도 무해하다.
 *
 * ⚠ **권한(`Notification.permission`)을 검사하지 않는다.** 알림을 *닫는* 데는 권한이 필요
 * 없는데, iOS PWA에서 페이지 컨텍스트의 `window.Notification` 상태가 기대와 다르면 그 가드가
 * 기능 전체를 조용히 막는다. 권한이 없으면 애초에 닫을 알림도 없다.
 *
 * ⚠ 통화 알림(`call-{callId}`)은 안읽음과 무관하다. tag 정규식을 느슨하게 고치면 수신 중인
 * 전화의 벨 알림이 다른 채널을 읽었다는 이유로 사라진다.
 *
 * ⚠ 호출부는 채널 목록을 한 번이라도 받은 뒤에만(`channelsSynced`) 불러야 한다. 기동 직후엔
 * 모든 채널이 `undefined`(=0)로 보여 아직 안 읽은 알림까지 지워 버린다.
 */
export async function syncChannelNotifications(unread: Record<number, number>): Promise<void> {
  try {
    // showWebNotification과 같은 이유로 `ready`가 아니라 `getRegistration()`을 쓴다.
    const reg = await navigator.serviceWorker?.getRegistration();
    if (!reg) return;

    // ① SW에 위임. controller는 SW가 아직 페이지를 제어하지 않는 첫 로드에 null이므로
    //    reg.active로 폴백한다. 수신부는 service-worker.js의 'SYNC_NOTIFICATIONS'.
    (navigator.serviceWorker.controller || reg.active)?.postMessage({
      type: 'SYNC_NOTIFICATIONS',
      unread,
    });

    // ② 페이지에서도 직접 닫는다 — SW가 잠들었거나 메시지가 유실되는 경우의 보루.
    if (!reg.getNotifications) return;
    const shown = await reg.getNotifications();
    for (const n of shown) {
      const matched = CHANNEL_TAG.exec(n.tag || '');
      if (!matched) continue;
      if ((unread[Number(matched[1])] || 0) > 0) continue;
      n.close();
    }
  } catch {
    // getNotifications 미지원(iOS Safari 이력)·권한 회수 등 — 알림 정리 실패가 호출부를 죽이면 안 된다.
  }
}
